import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import {
  Plus,
  Search,
  MoreVertical,
  Eye,
  Edit,
  Trash2,
  Archive,
  Power,
  PowerOff,
  Sparkles,
  ExternalLink,
  BarChart3,
  Loader2,
  Layout,
  Calendar,
  Users,
} from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import api from '@/services/api';

export default function AdminLandingPages() {
  const navigate = useNavigate();
  const [pages, setPages] = useState([]);
  const [webinars, setWebinars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedPage, setSelectedPage] = useState(null);
  const [selectedWebinar, setSelectedWebinar] = useState('');
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    fetchPages();
    fetchWebinars();
  }, [statusFilter]);

  const fetchPages = async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter && statusFilter !== 'all') {
        params.status = statusFilter;
      }
      const response = await api.get('/landing-pages', { params });
      setPages(response.data.items || []);
    } catch (error) {
      console.error('Error fetching landing pages:', error);
      toast.error('Failed to load landing pages');
    } finally {
      setLoading(false);
    }
  };

  const fetchWebinars = async () => {
    try {
      console.log('Fetching webinars from /admin/webinars...');
      // Fetch active webinars (published + draft status)
      const response = await api.get('/admin/webinars', { 
        params: { 
          per_page: 100,
          is_active_tab: true  // Get published + draft webinars
        } 
      });
      console.log('Webinars API response:', response.data);
      const webinarsList = response.data.webinars || [];
      console.log(`Loaded ${webinarsList.length} webinars`);
      setWebinars(webinarsList);
    } catch (error) {
      console.error('Error fetching webinars:', error);
      console.error('Error details:', error.response?.data);
      toast.error('Failed to load webinars');
    }
  };

  const handleCreate = async () => {
    if (!selectedWebinar) {
      toast.error('Please select a webinar');
      return;
    }

    setCreating(true);
    try {
      const response = await api.post('/landing-pages', {
        webinar_id: selectedWebinar,
      });
      toast.success('Landing page created successfully');
      setShowCreateDialog(false);
      setSelectedWebinar('');
      fetchPages();
      // Navigate to edit the new page
      navigate(`/admin/landing-pages/${response.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create landing page');
    } finally {
      setCreating(false);
    }
  };

  const handleEnable = async (pageId) => {
    try {
      await api.post(`/landing-pages/${pageId}/enable`);
      toast.success('Landing page enabled');
      fetchPages();
    } catch (error) {
      toast.error('Failed to enable landing page');
    }
  };

  const handleDisable = async (pageId) => {
    try {
      await api.post(`/landing-pages/${pageId}/disable`);
      toast.success('Landing page disabled');
      fetchPages();
    } catch (error) {
      toast.error('Failed to disable landing page');
    }
  };

  const handleArchive = async (pageId) => {
    try {
      await api.post(`/landing-pages/${pageId}/archive`);
      toast.success('Landing page archived');
      fetchPages();
    } catch (error) {
      toast.error('Failed to archive landing page');
    }
  };

  const handleDelete = async () => {
    if (!selectedPage) return;
    
    try {
      await api.delete(`/landing-pages/${selectedPage.id}`);
      toast.success('Landing page deleted');
      setShowDeleteDialog(false);
      setSelectedPage(null);
      fetchPages();
    } catch (error) {
      toast.error('Failed to delete landing page');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      active: 'bg-green-100 text-green-700',
      draft: 'bg-slate-100 text-slate-700',
      archived: 'bg-orange-100 text-orange-700',
    };
    return (
      <Badge className={styles[status] || styles.draft}>
        {status?.charAt(0).toUpperCase() + status?.slice(1) || 'Draft'}
      </Badge>
    );
  };

  const filteredPages = pages.filter((page) =>
    page.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    page.webinar_title?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Show all active webinars - allow creating landing pages for any webinar
  // If a webinar already has a landing page, the backend will handle the duplicate check
  const availableWebinars = webinars;

  return (
    <div className="space-y-6" data-testid="admin-landing-pages">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-navy-700">
            Landing Pages
          </h1>
          <p className="text-slate-500 mt-1">
            Create and manage marketing landing pages for your webinars
          </p>
        </div>
        <Button
          onClick={() => setShowCreateDialog(true)}
          className="bg-navy-600 hover:bg-navy-700"
          data-testid="create-landing-page-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Create Landing Page
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search landing pages..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="archived">Archived</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Pages Table */}
      <Card className="border-slate-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-navy-600" />
            </div>
          ) : filteredPages.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Page</TableHead>
                  <TableHead>Webinar</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-center">Views</TableHead>
                  <TableHead className="text-center">Conversions</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredPages.map((page) => (
                  <TableRow key={page.id}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-navy-100 flex items-center justify-center">
                          <Layout className="w-5 h-5 text-navy-600" />
                        </div>
                        <div>
                          <p className="font-medium text-navy-700 line-clamp-1">
                            {page.title}
                          </p>
                          <p className="text-xs text-slate-500">/{page.slug}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Link
                        to={`/admin/webinars/${page.webinar_id}`}
                        className="text-sm text-navy-600 hover:underline line-clamp-1"
                      >
                        {page.webinar_title}
                      </Link>
                    </TableCell>
                    <TableCell>{getStatusBadge(page.status)}</TableCell>
                    <TableCell className="text-center">
                      <span className="text-sm font-medium">{page.view_count || 0}</span>
                    </TableCell>
                    <TableCell className="text-center">
                      <span className="text-sm font-medium">{page.conversion_count || 0}</span>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-slate-500">
                        {page.created_at
                          ? format(new Date(page.created_at), 'MMM dd, yyyy')
                          : '-'}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={() => navigate(`/admin/landing-pages/${page.id}`)}
                          >
                            <Edit className="w-4 h-4 mr-2" />
                            Edit Page
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() =>
                              window.open(`/lp/${page.slug}`, '_blank')
                            }
                          >
                            <ExternalLink className="w-4 h-4 mr-2" />
                            Preview
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          {page.is_enabled ? (
                            <DropdownMenuItem onClick={() => handleDisable(page.id)}>
                              <PowerOff className="w-4 h-4 mr-2" />
                              Disable
                            </DropdownMenuItem>
                          ) : (
                            <DropdownMenuItem onClick={() => handleEnable(page.id)}>
                              <Power className="w-4 h-4 mr-2" />
                              Enable
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem onClick={() => handleArchive(page.id)}>
                            <Archive className="w-4 h-4 mr-2" />
                            Archive
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            className="text-red-600"
                            onClick={() => {
                              setSelectedPage(page);
                              setShowDeleteDialog(true);
                            }}
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-12">
              <Layout className="w-16 h-16 mx-auto text-slate-300 mb-4" />
              <h3 className="font-heading text-lg font-semibold text-navy-700 mb-2">
                No Landing Pages Yet
              </h3>
              <p className="text-slate-500 mb-6">
                Create your first AI-powered landing page for a webinar.
              </p>
              <Button
                onClick={() => setShowCreateDialog(true)}
                className="bg-navy-600 hover:bg-navy-700"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Landing Page
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Landing Page</DialogTitle>
            <DialogDescription>
              Select a webinar to create a marketing landing page for.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Select Webinar</Label>
              <Select value={selectedWebinar} onValueChange={setSelectedWebinar}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose a webinar..." />
                </SelectTrigger>
                <SelectContent>
                  {availableWebinars.length > 0 ? (
                    availableWebinars.map((webinar) => (
                      <SelectItem key={webinar.id} value={webinar.id}>
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-slate-400" />
                          <span className="truncate max-w-[300px]">
                            {webinar.title}
                          </span>
                        </div>
                      </SelectItem>
                    ))
                  ) : (
                    <SelectItem value="no-webinars" disabled>
                      All webinars have landing pages
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
            <div className="p-4 bg-navy-50 rounded-lg">
              <div className="flex items-start gap-3">
                <Sparkles className="w-5 h-5 text-navy-600 mt-0.5" />
                <div>
                  <p className="font-medium text-navy-700">AI-Powered Content</p>
                  <p className="text-sm text-slate-600">
                    After creation, use AI to generate compelling headlines, descriptions,
                    FAQs, and more.
                  </p>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleCreate}
              disabled={!selectedWebinar || creating}
              className="bg-navy-600 hover:bg-navy-700"
            >
              {creating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4 mr-2" />
                  Create Page
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Landing Page</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{selectedPage?.title}"? This action
              cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              <Trash2 className="w-4 h-4 mr-2" />
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
